import { createSlice } from '@reduxjs/toolkit'

import storage from '@/utils/storage'

import type { ApplicationState } from '@/shared/types/application'
import { UserInformation } from '@/shared/types/authentication'

const initialState: ApplicationState = {
    rawConfig: undefined,
    savedFilters: {},
    showAssistantInfo: true,
    config: undefined,
    darkTheme: false,
    hideDisclaimer: true,
    disableAutoAuthorize: undefined,
    queryParams: undefined
}

const getInitialState = () => {
    const userInformation = storage.getUserInformation()
    const systemDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false

    const mappedInitialState = { ...initialState, darkTheme: systemDark }

    if (userInformation) {
        mappedInitialState.darkTheme = storage.getColorTheme() ?? systemDark
        // If the user already has information stored under the local storage key 'applicationSettings',
        // read from applicationSettings and merge it with new savedFilters.
        if (userInformation['applicationSettings']) {
            delete userInformation['applicationSettings']['language']
            delete userInformation['applicationSettings']['showAssistantInfo']
        }

        mappedInitialState.savedFilters = { ...userInformation.applicationSettings || {}, ...userInformation.savedFilters || {} }
        mappedInitialState.queryParams = userInformation?.queryParams
        mappedInitialState.showAssistantInfo = userInformation?.showAssistantInfo ?? initialState.showAssistantInfo
        mappedInitialState.disableAutoAuthorize = userInformation?.disableAutoAuthorize
    }

    return mappedInitialState
}

export const applicationSlice = createSlice({
    name: 'application',
    initialState: getInitialState(),
    reducers: {
        updateRawConfig: (state, action) => {
            state.rawConfig = action.payload.rawConfig
        },
        updateConfig: (state, action) => {
            state.config = action.payload.config
        },
        toggleAppTheme: (state) => {
            state.darkTheme = !state.darkTheme
            storage.setColorTheme(state.darkTheme)
        },
        setDarkTheme: (state, action: { payload: boolean }) => {
            state.darkTheme = action.payload
        },
        setHideDisclaimer: (state, action) => {
            state.hideDisclaimer = action.payload
        },
        setIsNavOpen: (state, action) => {
            state.isNavOpen = action.payload
        },
        persistApplicationState: (state, action) => {
            Object.assign(state, action.payload)
            let userInformation = storage.getUserInformation()
            if (!userInformation) {
                userInformation = {}
            }
            userInformation = { ...userInformation, ...action.payload }
            storage.setUserInformation(userInformation as UserInformation)
        },
    },
})

export const {
    updateRawConfig,
    updateConfig,
    toggleAppTheme,
    setDarkTheme,
    setHideDisclaimer,
    setIsNavOpen,
    persistApplicationState,
} = applicationSlice.actions

export default applicationSlice.reducer
